import React from 'react'
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
} from "@mui/material";

// Refined reusable global modal component
const GlobalModal = ({
  open,
  onClose,
  onProceed,
  title,
  description,
  items = [],
  conclusion,
  checkboxLabel,
  checkboxChecked,
  onCheckboxChange,
  proceedLabel = "Continue to proceed",
  maxWidth = "xs",
  paperSx,
  backdropSx,
  titleSx,
  contentSx,
  actionsSx,
  proceedButtonSx,
  disableBackdropClose = true,
  gif,
  video, // Now explicitly supported
  videoWidth, // new: allow dynamic video width
  videoHeight, // new: allow dynamic video height
  modalWidth, // new: allow dynamic width
  modalHeight, // new: allow dynamic height
  contentMinHeight, // new: allow dynamic content min height
  contentMaxHeight, // new: allow dynamic content max height
}) => {
  const handleDialogClose = (event, reason) => {
    if (disableBackdropClose && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      return;
    }
    onClose?.(event, reason);
  };

  // Determine if we need a header (title) and/or footer (actions)
  const hasHeader = !!title;
  const hasFooter = !!onProceed || !!checkboxLabel;

  return (
    <Dialog
      open={!!open}
      onClose={handleDialogClose}
      aria-labelledby="global-modal-title"
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          background: "rgba(255,255,255,0.85)",
          borderRadius: 3,
          boxShadow: "0 8px 32px 0 rgba(0,0,0,0.10)",
          zIndex: 1302,
          position: "relative",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          display: "flex",
          flexDirection: "column",
          maxHeight: modalHeight || "90vh",
          width: modalWidth || undefined,
          minWidth: modalWidth ? modalWidth : undefined,
          ...paperSx,
        },
      }}
      BackdropProps={{
        sx: {
          // Give the modal background a shadow and blurring effect
          background: "rgba(0,0,0,.34)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          ...backdropSx,
        },
      }}
    >
      {/* Header */}
      {hasHeader && (
        <DialogTitle
          id="global-modal-title"
          sx={{
            textAlign: "center",
            fontWeight: 700,
            color: "#000",
            fontFamily: "Poppins, 'serif'",
            background: "rgba(255,255,255,0.85)",
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            py: 2,
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            ...titleSx,
          }}
        >
          {title}
        </DialogTitle>
      )}

      {/* Main Content with y-axis scroller */}
      <DialogContent
        sx={{
          textAlign: "center",
          flex: "1 1 auto",
          overflowY: "auto",
          mb: hasFooter ? 0 : 1,
          background: "rgba(255,255,255,0.85)",
          px: 2,
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: (!hasHeader && !hasFooter) ? "center" : "flex-start",
          minHeight: contentMinHeight !== undefined
            ? contentMinHeight
            : (!hasHeader && !hasFooter) ? "300px" : undefined,
          maxHeight: contentMaxHeight !== undefined
            ? contentMaxHeight
            : "60vh",
          width: "100%",
          ...contentSx,
        }}
      >
        {/* Video */}
        {video && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 2, width: "100%" }}>
            <video
              src={video}
              controls
              width={videoWidth}
              height={videoHeight}
              style={{
                maxWidth: videoWidth ? undefined : "100%",
                width: videoWidth ? videoWidth : "100%",
                borderRadius: 8,
                boxShadow: "0 2px 8px 0 rgba(0,0,0,0.08)",
                background: "#fff",
                height: videoHeight ? videoHeight : "auto",
                objectFit: "contain",
              }}
            >
              Your browser does not support the video tag.
            </video>
          </Box>
        )}

        {/* Description */}
        {description && (
          <Typography
            variant="body1"
            sx={{
              mb: 2,
              color: "#000",
              fontWeight: 600,
              fontFamily: "Poppins, 'serif'",
            }}
          >
            {description}
          </Typography>
        )}

        {/* Items and Conclusion */}
        {(items?.length || conclusion) && (
          <Box
            sx={{
              textAlign: "left",
              fontSize: "0.98rem",
              whiteSpace: "pre-line",
              mb: 2,
              background: "rgba(255,255,255,0.85)",
              borderRadius: 2,
              p: 2,
              color: "#000",
              fontFamily: "Poppins, 'serif'",
              boxShadow: "0 2px 8px 0 rgba(0,0,0,0.03)",
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
              width: "100%",
              maxWidth: "100%",
              ...(
                modalWidth
                  ? { maxWidth: "100%" }
                  : { maxWidth: 420 }
              ),
            }}
          >
            {Array.isArray(items) && items.length > 0 && (
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                {items.map((item, idx) => (
                  <li key={idx} style={{ marginBottom: 12 }}>
                    {item?.title && (
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#000" }}>
                        {item.title}
                      </Typography>
                    )}
                    {item?.description && (
                      <Typography variant="body2" sx={{ color: "#222", ml: 1 }}>
                        {item.description}
                      </Typography>
                    )}
                  </li>
                ))}
              </Box>
            )}
            {conclusion && (
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                {conclusion}
              </Typography>
            )}
          </Box>
        )}

        {/* Gif */}
        {gif && (
          <Box sx={{ mt: 2, display: "flex", justifyContent: "center", width: "100%" }}>
            <img
              src={gif}
              alt="modal-gif"
              style={{
                maxWidth: "100%",
                maxHeight: 180,
                borderRadius: 8,
                boxShadow: "0px",
                objectFit: "contain",
                width: "100%",
              }}
            />
          </Box>
        )}
      </DialogContent>

      {/* Footer (actions) */}
      {hasFooter && (
        <DialogActions
          sx={{
            flexDirection: "column",
            alignItems: "flex-start",
            pb: 2,
            pt: 0,
            background: "rgba(255,255,255,0.85)",
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            ...actionsSx,
          }}
        >
          {checkboxLabel && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!checkboxChecked}
                  onChange={(e) => onCheckboxChange?.(e.target.checked)}
                  sx={{
                    color: "#000",
                    "&.Mui-checked": { color: "#000" },
                  }}
                />
              }
              label={<span style={{ color: "#000", fontWeight: 600 }}>{checkboxLabel}</span>}
              sx={{ mt: 1, mb: 1, ml: 1, fontFamily: "Poppins, 'serif'", color: "#000" }}
            />
          )}

          {onProceed && (
            <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                onClick={onProceed}
                disabled={checkboxLabel ? !checkboxChecked : false}
                sx={{
                  backgroundColor: checkboxLabel && !checkboxChecked ? "#90caf9!important" : "#003366!important",
                  color: "#fff",
                  fontWeight: 700,
                  fontFamily: "Poppins, 'serif'",
                  boxShadow: "0 2px 8px 0 rgba(0,0,0,0.03)",
                  borderRadius: "8px",
                  padding: "8px 24px",
                  fontSize: "1rem",
                  border: `1px solid ${checkboxLabel && !checkboxChecked ? "#90caf9" : "#003366"}`,
                  opacity: checkboxLabel && !checkboxChecked ? 0.7 : 1,
                  transition: "all 0.2s",
                  "&:hover": { backgroundColor: "#002244" },
                  ...proceedButtonSx,
                }}
              >
                {proceedLabel}
              </Button>
            </Box>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default GlobalModal
